import { useMutation } from '@tanstack/react-query';
import { ScanSearch, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Select } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';

export type SalutationMeaning = 'COMPANY' | 'MR' | 'MRS' | 'FAMILY' | 'PERSON';
export type NameOrder = 'AUTO' | 'FIRST_LAST' | 'LAST_FIRST';

interface DetectedValue {
  value: string;
  count: number;
  examples: string[];
  suggestion: SalutationMeaning | null;
  companyShare: number;
}

/**
 * Anrede-Schlüsselzahlen aus Powerbird: Werte erkennen (Anzahl, Beispielnamen, Vorschlag) und je Zahl festlegen,
 * was sie bedeutet. Gespeichert wird mit der übrigen Powerbird-Einrichtung.
 */
export function SalutationMapCard({
  map,
  onMap,
  nameOrder,
  onNameOrder,
  meanings,
  configured,
}: {
  map: Record<string, SalutationMeaning>;
  onMap: (m: Record<string, SalutationMeaning>) => void;
  nameOrder: NameOrder;
  onNameOrder: (o: NameOrder) => void;
  meanings: { key: string; label: string }[];
  configured: boolean;
}) {
  const [values, setValues] = useState<DetectedValue[] | null>(null);
  const detect = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string; values: DetectedValue[] }>('/integrations/powerbird/salutations', { method: 'POST' }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.message, { duration: 10_000 });
        return;
      }
      setValues(r.values);
      toast.success(r.message);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  // erkannte Werte plus bereits gespeicherte, die gerade nicht vorkommen
  const rows: DetectedValue[] = [...(values ?? [])];
  for (const key of Object.keys(map)) {
    if (!rows.some((r) => r.value === key)) rows.push({ value: key, count: 0, examples: [], suggestion: null, companyShare: 0 });
  }
  const openSuggestions = (values ?? []).filter((v) => v.suggestion && !map[v.value]);
  const setOne = (key: string, meaning: SalutationMeaning | '') => {
    const next = { ...map };
    if (meaning) next[key] = meaning;
    else delete next[key];
    onMap(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Anrede (Schlüsselzahlen)</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <p className="text-muted-foreground">
          In Powerbird steht die Anrede als Zahl (Spalte <code>anrede</code> der Abfrage, Vorlage: <code>BriefAnrede</code>). Legen Sie fest, wofür jede Zahl steht: <em>Firma</em> wird Geschäftskunde,
          <em> Herr</em>, <em>Frau</em>, <em>Familie</em> und <em>Privatperson</em> werden Privatkunden – Name 1 wird zum Nachnamen, Name 2 zum Vornamen. Nicht zugeordnete Zahlen gelten als Firma.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled={!configured} loading={detect.isPending} onClick={() => detect.mutate()}>
            <ScanSearch /> Werte aus Powerbird erkennen
          </Button>
          {openSuggestions.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMap({ ...map, ...Object.fromEntries(openSuggestions.map((v) => [v.value, v.suggestion!])) })}
            >
              <Sparkles /> {openSuggestions.length} Vorschläge übernehmen
            </Button>
          )}
          {!configured && <span className="text-xs text-muted-foreground">Zuerst die Verbindung speichern.</span>}
        </div>
        {rows.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">Wert</th>
                  <th className="px-3 py-1.5 text-right font-medium">Kunden</th>
                  <th className="px-3 py-1.5 text-left font-medium">Beispiele</th>
                  <th className="px-3 py-1.5 text-left font-medium">Bedeutung</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.value || '(leer)'} className="border-t align-top">
                    <td className="px-3 py-2 font-mono">{r.value === '' ? <span className="text-muted-foreground">(leer)</span> : r.value}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{values ? r.count : '–'}</td>
                    <td className="max-w-md px-3 py-2 text-xs text-muted-foreground">
                      {r.examples.join(' · ') || (values ? '–' : 'noch nicht erkannt')}
                      {r.companyShare > 0 && <span className="ml-1">({r.companyShare} % sehen nach Firma aus)</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Select className="h-8 w-52" value={map[r.value] ?? ''} onChange={(e) => setOne(r.value, e.target.value as SalutationMeaning | '')} aria-label={`Bedeutung von ${r.value || 'leer'}`}>
                          <option value="">– nicht festgelegt (Firma) –</option>
                          {meanings.map((m) => (
                            <option key={m.key} value={m.key}>
                              {m.label}
                            </option>
                          ))}
                        </Select>
                        {r.suggestion && !map[r.value] && <Badge variant="outline">Vorschlag: {meanings.find((m) => m.key === r.suggestion)?.label}</Badge>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Alert>Noch keine Werte – mit „Werte aus Powerbird erkennen“ liest das Portal die vorhandenen Anrede-Zahlen mit Anzahl und Beispielnamen.</Alert>
        )}
        <label className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">Name bei Privatkunden:</span>
          <Select className="h-8 w-80" value={nameOrder} onChange={(e) => onNameOrder(e.target.value as NameOrder)} aria-label="Reihenfolge im Namen">
            <option value="AUTO">Name 1 = Nachname, Name 2 = Vorname (Powerbird)</option>
            <option value="FIRST_LAST">alles in Name 1: Vorname Nachname</option>
            <option value="LAST_FIRST">alles in Name 1: Nachname Vorname</option>
          </Select>
        </label>
        <p className="text-xs text-muted-foreground">Wirksam ab dem nächsten Abgleich – „Speichern“ und dann „Jetzt abgleichen“.</p>
      </CardContent>
    </Card>
  );
}
